<template>
  <div>
    <h4 class="h4">
      <b-badge variant="primary">{{ selectedNetwork.text }}</b-badge>
    </h4>
    <p class="text-center h6 font-weight-bolder mb-1">Configuración IP</p>
    <div class="row mb-1">
      <div class="col-6">
        <b-form-group label="Tipo de IP">
          <v-select
            v-model="selectedNetwork.IPv4Type"
            :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
            :options="types"
            :clearable="false"
            input-id="ip-type"
            :reduce="(val) => val.value"
            @input="ipTypeChange"
          />
        </b-form-group>
      </div>
      <div class="col-6">
        <b-form-group label="Dirección IP">
          <IpAddressInput
            :value="selectedNetwork.IPv4Address"
            @input="setAddress($event, 'IPv4Address')"
            :disabled="disabledIpsInput"
          />
          <div v-if="errors.IPv4Address" class="d-block invalid-feedback">
            {{ errors.IPv4Address }}
          </div>
        </b-form-group>
      </div>
      <div class="col-6">
        <b-form-group label="Máscara de subred">
          <IpAddressInput
            :value="selectedNetwork.IPv4SubnetMask"
            @input="setAddress($event, 'IPv4SubnetMask')"
            :disabled="disabledIpsInput"
          />
          <div v-if="errors.IPv4SubnetMask" class="d-block invalid-feedback">
            {{ errors.IPv4SubnetMask }}
          </div>
        </b-form-group>
      </div>
      <div class="col-6">
        <b-form-group label="Puerta de enlace">
          <IpAddressInput
            :value="selectedNetwork.IPv4Gateway"
            @input="setAddress($event, 'IPv4Gateway')"
            :disabled="disabledIpsInput"
          />
          <div v-if="errors.IPv4Gateway" class="d-block invalid-feedback">
            {{ errors.IPv4Gateway }}
          </div>
        </b-form-group>
      </div>
    </div>
    <p class="text-center h6 font-weight-bolder mb-1">Configuración DNS</p>
    <div class="row mb-1">
        <div class="col-6">
          <b-form-group label="Tipo de DNS">
            <v-select
              v-model="selectedNetwork.DnsType"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="types"
              :clearable="false"
              input-id="dns-type"
              :disabled="!disabledIpsInput"
              :reduce="(val) => val.value"
            />
          </b-form-group>
        </div>
        <div class="col-6">
          <b-form-group label="Dirección DNS">
            <IpAddressInput
              :value="selectedNetwork.PrimaryDNS"
              @input="setAddress($event, 'PrimaryDNS')"
              :disabled="disabledDnsInput"
            />
            <div v-if="errors.PrimaryDNS" class="d-block invalid-feedback">
              {{ errors.PrimaryDNS }}
            </div>
          </b-form-group>
        </div>
      </div>
    <div class="text-right">
      <b-button
        type="button"
        class="mr-1"
        variant="outline-danger"
        @click="cancel()"
      >
        Cancelar
      </b-button>
      <b-button
        type="button"
        variant="primary"
        @click="accept()"
      >
        Aceptar
      </b-button>
    </div>
  </div>
</template>

<script>
import { computed,ref,onMounted } from "@vue/composition-api";
import IpAddressInput from "@/components/InputIp/IpAddressInput.vue";
import vSelect from "vue-select";

export default {
  props: {
    selectedNetwork:{
      type: Object
    }
  },
  components: {
    IpAddressInput,
    vSelect
  },
  setup (props, { emit }) {
    //HOOKS
    onMounted(() => {
      originalNetwork.value = { ...props.selectedNetwork }
    });

    //DATA
    const errors = ref({
      IPv4Address: null,
      IPv4SubnetMask: null,
      IPv4Gateway: null,
      PrimaryDNS: null
    });

    const hasErrors = ref(false);

    const types = [
      { label: 'Manual', value: 'Manual' },
      { label: 'DHCP', value: 'DHCP' }
    ];

    const originalNetwork = ref({});

    //COMPUTED
    const disabledDnsInput = computed(() => (props.selectedNetwork.IPv4Type === 'DHCP') && (props.selectedNetwork.DnsType === 'DHCP'));

    const disabledIpsInput = computed(() => props.selectedNetwork.IPv4Type === 'DHCP');

    //METHODS
    const accept = () => {
      validateIps();

      if (hasErrors.value) {
        return
      }

      emit('network-config-close');
    }

    const cancel = () => {
      resetNetworkData();
      emit('network-config-close');
    }

    const ipTypeChange = (value) => {
      props.selectedNetwork.DnsType = value;

      if (value === 'DHCP') {
        props.selectedNetwork.IPv4Address    = '0.0.0.0';
        props.selectedNetwork.IPv4SubnetMask = '0.0.0.0';
        props.selectedNetwork.IPv4Gateway    = '0.0.0.0';
        props.selectedNetwork.PrimaryDNS     = '0.0.0.0';
      } else {
        props.selectedNetwork.IPv4Address = originalNetwork.value.IPv4Address;
        props.selectedNetwork.IPv4SubnetMask = originalNetwork.value.IPv4SubnetMask;
        props.selectedNetwork.IPv4Gateway = originalNetwork.value.IPv4Gateway;
        props.selectedNetwork.PrimaryDNS = originalNetwork.value.PrimaryDNS;
      }
    }

    const resetNetworkData = () => {
      props.selectedNetwork.IPv4Type = originalNetwork.value.IPv4Type;
      props.selectedNetwork.IPv4Address = originalNetwork.value.IPv4Address;
      props.selectedNetwork.IPv4SubnetMask = originalNetwork.value.IPv4SubnetMask;
      props.selectedNetwork.IPv4Gateway = originalNetwork.value.IPv4Gateway;
      props.selectedNetwork.DnsType = originalNetwork.value.DnsType;
      props.selectedNetwork.PrimaryDNS = originalNetwork.value.PrimaryDNS;
    }

    const setAddress = (value, field) => {
      props.selectedNetwork[field] = value;
    }

    const validateIps = () => {
      const fields = {
        IPv4Address: 'La dirección IP es inválida',
        IPv4SubnetMask: 'La máscara de subred es inválida',
        IPv4Gateway: 'La puerta de enlace es inválida',
        PrimaryDNS: 'El DNS es inválido'
      };

      const ipv4Fields = [
        'IPv4Address',
        'IPv4SubnetMask',
        'IPv4Gateway',
      ];

      for (const key in errors.value) {
        const keyInvludesIpv4Fields = ipv4Fields.includes(key);
        const ipv4TypeIsNotManual   = (props.selectedNetwork.IPv4Type !== 'Manual');

        if (keyInvludesIpv4Fields && ipv4TypeIsNotManual) {
          errors.value[key] = null;
          continue;
        }

        const dnsTypeIsManual = (props.selectedNetwork.DnsType === 'Manual');
        const isPrimaryDnsKey = (key === 'PrimaryDNS');
        const dnsInvalid      = (props.selectedNetwork.PrimaryDNS === '0.0.0.0');

        if (dnsTypeIsManual && isPrimaryDnsKey && dnsInvalid) {
          errors.value[key] = fields[key];
          continue;
        }

        const ip = props.selectedNetwork[key];

        if (isPrimaryDnsKey && !dnsTypeIsManual && ip === '0.0.0.0') {
          errors.value[key] = null;
          continue;
        }


        if ((!ip || ip === '0.0.0.0')) {
          errors.value[key] = fields[key];
          continue;
        }

        const parts = ip.split('.');

        if (parts.length !== 4) {
          errors.value[key] = fields[key];
          continue;
        }

        const isFieldValid = parts.every(part => {
          if (!/^\d{1,3}$/.test(part)) {
            return false;
          }

          const value = Number(part);

          return value >= 0 && value <= 255;
        });

        errors.value[key] = isFieldValid
          ? null
          : fields[key];
      }

      hasErrors.value = Object.values(errors.value).some(
        value => value !== null
      );
    }

    return {
      errors,
      types,

      disabledDnsInput,
      disabledIpsInput,

      accept,
      cancel,
      ipTypeChange,
      originalNetwork,
      setAddress
    }
  }
}
</script>

<style lang="scss" scoped>
.content-network-data {
  border: 1px solid rgba(34, 41, 47, 0.2);
}
</style>
